import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr, apiGet, getApiOnline } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, Plus, Dumbbell, ChevronUp, ChevronDown, Trash2, Lock, CalendarClock, RotateCw, MoreVertical, Download, Eye, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDate, fmtTimestamp } from "@/lib/format";
import { inputCls, useKeyboardInset } from "./fields";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  TEMPLATE_TYPES, TEMPLATE_LABELS, CONTEXT_LABELS, STATE_LABELS, templateLabel,
  defaultTitleFor, makeTextBlock, makeExerciseBlock, moveBlock, removeBlock, updateBlock, addBlock,
  blockHasContent, toPayloadBlocks, buildCreateBody, saveResult, needsConnectivityProbe, formatLinkedSession,
  DIARY_IMPORT_FIELDS, HOMEWORK_IMPORT_FIELDS, importCloseAllowed, runImportConfirm, performImport,
  toClientFacing, clientFacingIsEmpty, EXERCISE_PREVIEW_FIELDS, makeTitlePreselect, groupDeliveryFamilies,
  buildClientSharePayload,
} from "./clientDeliveryModel";

const HW_STATUS_LABELS = { active: "Attivo", completed: "Completato", cancelled: "Annullato" };

// T4.2: Copy / native Share. Both derive from the SAME client-facing payload (no raw-data path).
// Draft usage passes the live editor {title, blocks}; finalized passes the frozen document.
const ShareActions = ({ data }) => {
  const cf = toClientFacing(data);
  const disabled = clientFacingIsEmpty(cf);
  const payload = () => buildClientSharePayload(cf);

  const writeClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) { await navigator.clipboard.writeText(text); return; }
    throw new Error("clipboard unavailable");
  };

  const doCopy = async () => {
    try { await writeClipboard(payload().text); toast.success("Testo copiato"); }
    catch { toast.error("Impossibile copiare il testo"); }
  };

  const fallbackCopy = async () => {
    try { await writeClipboard(payload().text); toast("Condivisione non disponibile: testo copiato"); }
    catch { toast.error("Impossibile copiare il testo"); }
  };

  const doShare = async () => {
    const p = payload();
    if (navigator.share) {
      try { await navigator.share({ title: p.title, text: p.text }); toast("Condivisione avviata"); }
      catch (e) { if (!(e && e.name === "AbortError")) await fallbackCopy(); } // cancel is neutral
      return;
    }
    await fallbackCopy();
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" data-testid="delivery-copy-text-btn" disabled={disabled} onClick={doCopy}><Copy className="h-4 w-4 mr-1" />Copia testo</Button>
      <Button variant="outline" className="rounded-full" data-testid="delivery-share-btn" disabled={disabled} onClick={doShare}><Share2 className="h-4 w-4 mr-1" />Condividi</Button>
    </>
  );
};

const SAVE_LABEL = { idle: "", unsaved: "Modifiche non salvate", saving: "Salvataggio…", saved: "Salvato", error: "Errore di salvataggio", conflict: "Modificata altrove" };
const SAVE_CLS = { idle: "text-muted-foreground", unsaved: "text-amber-600", saving: "text-amber-600", saved: "text-emerald-600", error: "text-destructive", conflict: "text-destructive" };

// Local long-text field: comfortable min height, grows with content, scrolls — never clips.
const LongText = ({ value, onChange, testid, placeholder }) => {
  const ref = useRef(null);
  const resize = () => { const el = ref.current; if (!el) return; el.style.height = "auto"; el.style.height = Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.6)) + "px"; };
  useEffect(() => { resize(); }, [value]);
  return <textarea ref={ref} data-testid={testid} rows={4} placeholder={placeholder}
    className={`${inputCls} resize-y min-h-[120px] overflow-y-auto leading-relaxed`} value={value || ""}
    onChange={(e) => onChange(e.target.value)} onInput={resize} />;
};

const EXERCISE_FIELDS = [
  ["name", "Nome esercizio", false],
  ["goal", "Obiettivo", true],
  ["instructions", "Come eseguirlo", true],
  ["when_to_use", "Quando utilizzarlo", true],
  ["frequency", "Frequenza", false],
  ["duration", "Durata", false],
  ["cautions", "Attenzioni / Note", true],
];

const Badge = ({ children, cls, testid }) => (
  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 shrink-0 ${cls}`} data-testid={testid}>{children}</span>
);

const LinkedSessionLine = ({ ls }) => (
  <>
    <div className="font-semibold break-words">{ls.title}</div>
    <div className="text-xs text-muted-foreground">
      {[ls.date ? fmtDate(ls.date) : null, ls.time || null, ls.status || null].filter(Boolean).join(" · ")}
    </div>
  </>
);

// ---------------- Creation flow ----------------
const CreateFlow = ({ dogId, onCancel, onCreated }) => {
  const [step, setStep] = useState("template");
  const [templateType, setTemplateType] = useState(null);
  const [context, setContext] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (context !== "session") return;
    apiGet(`/dogs/${dogId}/lessons`).then(({ data }) => setSessions(data || [])).catch(() => setSessions([]));
  }, [context, dogId]);

  const submit = async (ctx, sid) => {
    setBusy(true);
    try {
      const body = buildCreateBody({ templateType, context: ctx, sessionId: sid, title: defaultTitleFor(templateType) });
      const { data } = await api.post(`/dogs/${dogId}/client-deliveries`, body);
      onCreated(data);
    } catch (e) {
      toast.error(apiErr(e, "Creazione non riuscita. Riprova."));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="delivery-create-flow">
      <Button size="sm" variant="ghost" className="rounded-full" data-testid="delivery-create-back"
        onClick={() => (step === "context" ? setStep("template") : onCancel())}>
        <ArrowLeft className="h-4 w-4 mr-1" />Indietro
      </Button>

      {step === "template" && (
        <div className="space-y-3" data-testid="delivery-template-step">
          <div>
            <h3 className="font-head text-lg font-bold">Nuova consegna</h3>
            <p className="text-sm text-muted-foreground">Scegli il tipo di documento da preparare.</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {TEMPLATE_TYPES.map((tt) => (
              <button key={tt} type="button" data-testid={`delivery-template-${tt}`} onClick={() => { setTemplateType(tt); setStep("context"); }}
                className="text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors">
                <div className="font-semibold">{TEMPLATE_LABELS[tt]}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === "context" && (
        <div className="space-y-4" data-testid="delivery-context-step">
          <div>
            <h3 className="font-head text-lg font-bold">{TEMPLATE_LABELS[templateType]}</h3>
            <p className="text-sm text-muted-foreground">A cosa si riferisce questa consegna?</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <button type="button" data-testid="delivery-context-general" disabled={busy}
              onClick={() => { setContext("general"); submit("general", null); }}
              className="text-left rounded-2xl border border-border bg-card p-4 hover:border-primary transition-colors disabled:opacity-50">
              <div className="font-semibold">{CONTEXT_LABELS.general}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Materiale generale sul cane o sul percorso.</div>
            </button>
            <button type="button" data-testid="delivery-context-session" disabled={busy}
              onClick={() => setContext("session")}
              className={`text-left rounded-2xl border p-4 transition-colors disabled:opacity-50 ${context === "session" ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary"}`}>
              <div className="font-semibold">{CONTEXT_LABELS.session}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Collegata a una prestazione già registrata.</div>
            </button>
          </div>

          {context === "session" && (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="delivery-session-picker">
              <div className="text-sm font-medium">Seleziona la prestazione</div>
              {sessions === null && <p className="text-sm text-muted-foreground">Caricamento…</p>}
              {sessions !== null && sessions.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="delivery-no-sessions">Nessuna prestazione disponibile da collegare.</p>
              )}
              {sessions !== null && sessions.length > 0 && (
                <div className="space-y-2">
                  {sessions.map((s) => (
                    <button key={s.session_id} type="button" data-testid={`delivery-session-${s.session_id}`} disabled={busy}
                      onClick={() => submit("session", s.session_id)}
                      className="w-full text-left rounded-xl border border-border p-3 hover:border-primary transition-colors disabled:opacity-50">
                      <div className="font-semibold break-words">{s.service_type || "Prestazione"}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Keyboard-aware persistent status/nav bar: on desktop sticky at the bottom; when the mobile
// software keyboard opens (visualViewport shrinks) it pins just above the keyboard so the save
// state stays visible while typing. No hard-coded keyboard heights.
const DeliveryStatusBar = ({ readOnly, saveState, onBack, onRetry }) => {
  const inset = useKeyboardInset();
  const kbOpen = inset > 120;
  const status = !readOnly ? <span className={`text-xs font-semibold ${SAVE_CLS[saveState]}`} data-testid="delivery-save-state">{SAVE_LABEL[saveState]}</span> : null;
  const retry = !readOnly && saveState === "error"
    ? <Button size="sm" variant="outline" className="rounded-full" data-testid="delivery-retry" onClick={onRetry}><RotateCw className="h-3.5 w-3.5 mr-1" />Riprova</Button>
    : null;
  const inner = (
    <>
      <Button size="sm" variant="ghost" className="rounded-full" data-testid="delivery-back" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Consegne</Button>
      <div className="ml-auto flex items-center gap-2">{status}{retry}</div>
    </>
  );
  if (kbOpen) return (
    <div data-testid="delivery-statusbar" className="fixed left-0 right-0 z-[60] px-4 py-2 bg-background border-t border-border flex items-center gap-2 shadow-[0_-6px_16px_rgba(0,0,0,0.12)]" style={{ bottom: inset }}>
      {inner}
    </div>
  );
  return (
    <div data-testid="delivery-statusbar" className="sticky bottom-[64px] lg:bottom-0 z-40 -mx-4 px-4 py-2.5 bg-background/95 backdrop-blur border-t border-border flex items-center gap-2" style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}>
      {inner}
    </div>
  );
};

// ---------------- T3 import dialog (explicit "Aggiungi da K9") ----------------
const ImportDialog = ({ dogId, onClose, onImport }) => {
  const [cat, setCat] = useState(null);
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  const [fields, setFields] = useState([]);
  const [busy, setBusyState] = useState(false);
  const busyRef = useRef(false);
  const setBusy = (v) => { busyRef.current = v; setBusyState(v); };

  useEffect(() => {
    if (!cat) return;
    setList(null); setSel(null); setFields([]);
    const url = cat === "homework" ? `/dogs/${dogId}/client-delivery-sources/homework` : `/dogs/${dogId}/client-delivery-sources/diary`;
    apiGet(url).then(({ data }) => setList(data || [])).catch(() => setList([]));
  }, [cat, dogId]);

  const toggleField = (k) => setFields((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const buildPayload = () => (cat === "homework"
    ? { source_type: "homework", source_id: sel.source_id }
    : { source_type: "lesson_diary", source_id: sel.source_id, selected_fields: fields });
  const confirm = () => {
    if (!sel) return;
    return runImportConfirm({ getBusy: () => busyRef.current, setBusy, importFn: () => onImport(buildPayload()), onClose });
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && importCloseAllowed(busy)) onClose(); }}>
      <DialogContent data-testid="import-dialog" className="max-w-lg w-[calc(100%-1.5rem)] max-h-[85dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>Aggiungi da K9</DialogTitle></DialogHeader>
        {!cat && (
          <div className="grid gap-2">
            <Button variant="outline" className="justify-start rounded-xl" data-testid="import-cat-homework" onClick={() => setCat("homework")}>Compiti assegnati</Button>
            <Button variant="outline" className="justify-start rounded-xl" data-testid="import-cat-diary" onClick={() => setCat("diary")}>Diario lezioni</Button>
          </div>
        )}
        {cat && (
          <div className="space-y-3">
            <button type="button" className="text-sm text-muted-foreground inline-flex items-center gap-1 disabled:opacity-50" data-testid="import-back" disabled={busy} onClick={() => { setCat(null); setSel(null); }}><ArrowLeft className="h-4 w-4" />Sorgenti</button>
            {list === null && <p className="text-sm text-muted-foreground" data-testid="import-loading">Caricamento…</p>}
            {list !== null && list.length === 0 && <p className="text-sm text-muted-foreground" data-testid="import-empty">Nessun contenuto disponibile per questo cane.</p>}
            {list && list.length > 0 && !sel && (
              <div className="space-y-2" data-testid="import-list">
                {list.map((it) => (
                  <button key={it.source_id} type="button" data-testid={`import-item-${it.source_id}`} onClick={() => { setSel(it); setFields([]); }}
                    className="w-full text-left rounded-xl border border-border p-3 hover:border-primary transition-colors">
                    <div className="font-semibold break-words">{cat === "homework" ? (it.fields.title || "(senza titolo)") : (it.session_context?.service_type || "Diario")}</div>
                    <div className="text-xs text-muted-foreground">{[it.session_context?.date ? fmtDate(it.session_context.date) : null, it.session_context?.start_time || null, cat === "homework" ? (HW_STATUS_LABELS[it.status] || it.status) : null].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
              </div>
            )}
            {sel && cat === "homework" && (
              <div className="space-y-2" data-testid="import-preview">
                <div className="text-sm font-medium">Verrà copiato in un blocco Esercizio:</div>
                {HOMEWORK_IMPORT_FIELDS.map(([k, label]) => (
                  <div key={k}><div className="text-xs text-muted-foreground">{label}</div><div className="text-sm whitespace-pre-line break-words">{sel.fields[k] || "—"}</div></div>
                ))}
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" disabled={busy} onClick={() => setSel(null)}>Indietro</Button>
                  <Button className="rounded-full" data-testid="import-confirm" disabled={busy} onClick={confirm}>Importa</Button>
                </div>
              </div>
            )}
            {sel && cat === "diary" && (
              <div className="space-y-2" data-testid="import-preview">
                <div className="text-sm font-medium">Seleziona i campi da copiare:</div>
                {DIARY_IMPORT_FIELDS.map(([k, label]) => (
                  <label key={k} className="block rounded-xl border border-border p-2 cursor-pointer" data-testid={`import-field-${k}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" className="h-4 w-4 cursor-pointer" data-testid={`import-field-cb-${k}`} checked={fields.includes(k)} onChange={() => toggleField(k)} />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line break-words">{sel.fields[k] || "—"}</div>
                  </label>
                ))}
                <div className="flex gap-2 justify-end pt-2">
                  <Button variant="ghost" disabled={busy} onClick={() => setSel(null)}>Indietro</Button>
                  <Button className="rounded-full" data-testid="import-confirm" disabled={busy || fields.length === 0} onClick={confirm}>Importa</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- T4.1 client-facing preview (derives from canonical content only) ----------------
const PreviewDialog = ({ data, onClose }) => {
  const cf = toClientFacing(data);
  const empty = clientFacingIsEmpty(cf);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="preview-dialog" className="max-w-lg w-[calc(100%-1.5rem)] max-h-[85dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>Anteprima cliente</DialogTitle></DialogHeader>
        {empty ? (
          <p className="text-sm text-muted-foreground" data-testid="preview-empty">Questa consegna non contiene ancora contenuti da mostrare al cliente.</p>
        ) : (
          <div className="space-y-4" data-testid="preview-content">
            <div className="flex items-baseline justify-between gap-2">
              {cf.title && <h2 className="text-lg font-semibold break-words" data-testid="preview-title">{cf.title}</h2>}
              {cf.revision_number ? <span className="text-xs text-muted-foreground shrink-0">Rev. {cf.revision_number}</span> : null}
            </div>
            {cf.blocks.map((b, i) => (
              <div key={i} className="space-y-1" data-testid={`preview-block-${i}`}>
                {b.type === "TEXT" ? (
                  <>
                    {b.title && <div className="font-semibold break-words">{b.title}</div>}
                    {b.content && <div className="text-sm whitespace-pre-line break-words leading-relaxed">{b.content}</div>}
                  </>
                ) : (
                  <div className="rounded-xl border border-border p-3 space-y-1.5">
                    {b.fields.map((f) => (
                      <div key={f.key} className="text-sm">
                        <span className="text-xs font-medium text-muted-foreground">{f.label}: </span>
                        <span className={`whitespace-pre-line break-words ${f.key === "name" ? "font-semibold" : ""}`}>{f.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        {!empty && (
          <div className="flex flex-wrap gap-2 pt-3 border-t border-border" data-testid="preview-share-actions">
            <ShareActions data={data} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ---------------- Editor (draft) + read-only (finalized) ----------------
const DeliveryEditor = ({ deliveryId, onBack, onOpenLesson, onNavigate, titlePreselect, guardRef }) => {
  const [loaded, setLoaded] = useState(false);
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [saveState, setSaveState] = useState("idle");
  const [exitPrompt, setExitPrompt] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [linkedSession, setLinkedSession] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const busyActionRef = useRef(false);

  const titleRef = useRef("");
  const blocksRef = useRef([]);
  const versionRef = useRef(0);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const editSeq = useRef(0);
  const timer = useRef(null);
  const pendingLeave = useRef(null);
  const titleSelRef = useRef(makeTitlePreselect(titlePreselect));

  const readOnly = doc?.state === "finalized";

  const reload = useCallback(async () => {
    const { data } = await apiGet(`/client-deliveries/${deliveryId}`);
    setDoc(data);
    setTitle(data.title || ""); titleRef.current = data.title || "";
    const bl = (data.blocks || []).map((b) => ({ ...b }));
    setBlocks(bl); blocksRef.current = bl;
    versionRef.current = data.record_version || 0;
    dirtyRef.current = false;
    setConflict(false);
    setSaveState("idle");
  }, [deliveryId]);

  useEffect(() => { reload().finally(() => setLoaded(true)); }, [reload]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  // Resolve the canonical linked prestation (read-only; never mutated).
  useEffect(() => {
    const sid = doc?.session_id;
    if (!sid) { setLinkedSession(null); return; }
    apiGet(`/sessions/${sid}`).then(({ data }) => setLinkedSession(data)).catch(() => setLinkedSession(null));
  }, [doc?.session_id]);

  // Serialized save: one PUT at a time; a newer edit during a request keeps it dirty.
  const doSave = useCallback(async () => {
    if (readOnly || savingRef.current || !dirtyRef.current) return;
    savingRef.current = true;
    setSaveState("saving");
    const seqAtSend = editSeq.current;
    try {
      // Self-heal a stale offline latch after reconnection before attempting the write.
      if (needsConnectivityProbe(getApiOnline(), navigator.onLine)) {
        try { await api.get(`/client-deliveries/${deliveryId}`); } catch (_) { /* best effort */ }
      }
      const { data } = await api.put(`/client-deliveries/${deliveryId}`, {
        title: titleRef.current,
        blocks: toPayloadBlocks(blocksRef.current),
        record_version: versionRef.current,
      });
      versionRef.current = data.record_version || 0;
      savingRef.current = false;
      const r = saveResult({ status: "ok", seqChanged: editSeq.current !== seqAtSend });
      if (r.clearDirty) dirtyRef.current = false;
      setSaveState(r.saveState);
      if (r.resave) { if (timer.current) clearTimeout(timer.current); timer.current = setTimeout(() => { doSave().catch(() => {}); }, 400); }
    } catch (e) {
      savingRef.current = false;
      const status = e?.response?.status === 409 ? "conflict" : "error";
      if (status === "conflict") setConflict(true);
      setSaveState(saveResult({ status }).saveState);
      throw e;
    }
  }, [deliveryId, readOnly]);

  // Auto-recover: when the browser reconnects, retry a pending save (no mutation queue).
  useEffect(() => {
    const onOnline = () => { if (dirtyRef.current && !savingRef.current) { setSaveState("unsaved"); doSave().catch(() => {}); } };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [doSave]);

  const markDirty = () => {
    dirtyRef.current = true;
    editSeq.current += 1;
    setSaveState("unsaved");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { doSave().catch(() => {}); }, 800);
  };

  const onTitle = (v) => { setTitle(v); titleRef.current = v; markDirty(); };
  const commitBlocks = (next) => { setBlocks(next); blocksRef.current = next; markDirty(); };
  const editBlockField = (id, k, v) => commitBlocks(updateBlock(blocksRef.current, id, { [k]: v }));
  const doMove = (i, dir) => commitBlocks(moveBlock(blocksRef.current, i, dir));
  const doRemove = (id) => commitBlocks(removeBlock(blocksRef.current, id));
  const doAddText = () => commitBlocks(addBlock(blocksRef.current, makeTextBlock("")));
  const doAddExercise = () => commitBlocks(addBlock(blocksRef.current, makeExerciseBlock("")));

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    if (dirtyRef.current && !savingRef.current) await doSave();
  }, [doSave]);

  // Register a navigation guard so DogDetail tab switches can't silently discard a dirty draft.
  useEffect(() => {
    if (!guardRef) return undefined;
    if (readOnly) { guardRef.current = null; return undefined; }
    guardRef.current = {
      isDirty: () => dirtyRef.current || savingRef.current || !!timer.current,
      flush: async () => { await flush(); },
      discard: () => { dirtyRef.current = false; if (timer.current) { clearTimeout(timer.current); timer.current = null; } },
    };
    return () => { if (guardRef) guardRef.current = null; };
  }, [guardRef, readOnly, flush]);

  const leaveTo = async (action) => {
    if (readOnly || (!dirtyRef.current && !timer.current && !savingRef.current)) return action();
    pendingLeave.current = action;
    try { await flush(); if (dirtyRef.current) setExitPrompt(true); else { pendingLeave.current = null; action(); } }
    catch { setExitPrompt(true); }
  };
  const handleBack = () => leaveTo(onBack);
  const openLesson = () => doc?.session_id && leaveTo(() => onOpenLesson(doc.session_id));

  const applyDoc = (data) => {
    setDoc(data);
    setTitle(data.title || ""); titleRef.current = data.title || "";
    const bl = (data.blocks || []).map((b) => ({ ...b })); setBlocks(bl); blocksRef.current = bl;
    versionRef.current = data.record_version || 0;
    dirtyRef.current = false; setSaveState("saved");
  };

  // Authoritative import via the safe coordinator: preliminary flush must succeed (no racing
  // with autosave), a flush/dirty failure never sends the POST nor claims success.
  const runImport = (payload) => performImport({
    flush,
    isDirty: () => dirtyRef.current,
    post: async () => (await api.post(`/client-deliveries/${deliveryId}/imports`, { ...payload, record_version: versionRef.current })).data,
    onSuccess: (data) => { applyDoc(data); toast.success("Contenuto importato"); },
    onConflict: () => { setConflict(true); setSaveState("conflict"); toast.error("La consegna è stata modificata altrove. Ricarica."); },
    onError: (kind) => {
      if (kind === "flush") toast.error("Salvataggio non riuscito. Riprova prima di importare.");
      else if (kind === "dirty") toast.error("Salva le modifiche prima di importare.");
      else toast.error("Importazione non riuscita.");
    },
  });

  // Finalize: flush pending edits first (never finalize a stale version), then the authoritative
  // transition via the existing endpoint; adopt the finalized server doc exactly once.
  const runFinalize = async () => {
    if (busyActionRef.current) return;
    busyActionRef.current = true;
    try {
      await performImport({
        flush,
        isDirty: () => dirtyRef.current,
        post: async () => (await api.post(`/client-deliveries/${deliveryId}/finalize`, { record_version: versionRef.current })).data,
        onSuccess: (data) => { applyDoc(data); setFinalizeOpen(false); toast.success("Consegna finalizzata"); },
        onConflict: () => { setConflict(true); setSaveState("conflict"); setFinalizeOpen(false); toast.error("La consegna è stata modificata altrove. Ricarica."); },
        onError: (kind) => { toast.error(kind === "flush" || kind === "dirty" ? "Salva le modifiche prima di finalizzare." : "Finalizzazione non riuscita."); },
      });
    } finally { busyActionRef.current = false; }
  };

  // New revision from a finalized revision (Rev1 -> Rev2 draft); navigate to the new draft.
  const runRevision = async () => {
    if (busyActionRef.current) return;
    busyActionRef.current = true;
    try {
      const { data } = await api.post(`/client-deliveries/${deliveryId}/revisions`);
      if (guardRef) guardRef.current = null;
      toast.success("Nuova revisione creata");
      if (onNavigate) onNavigate(data.id);
    } catch (e) {
      toast.error(apiErr(e, "Creazione revisione non riuscita."));
    } finally { busyActionRef.current = false; }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/client-deliveries/${deliveryId}`);
      dirtyRef.current = false;
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      if (guardRef) guardRef.current = null;
      toast.success("Bozza eliminata");
      onBack();
    } catch (e) { toast.error(apiErr(e, "Eliminazione non riuscita.")); }
  };

  if (!loaded) return <p className="text-sm text-muted-foreground" data-testid="delivery-loading">Caricamento…</p>;

  const ls = formatLinkedSession(linkedSession);

  return (
    <div className="space-y-4 pb-4" data-testid="delivery-editor">
      {/* Meta row (scrolls) */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="delivery-meta">
        <span className="text-xs text-muted-foreground">{templateLabel(doc.template_type)}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs text-muted-foreground">{CONTEXT_LABELS[doc.document_type] || "Generale"}</span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs text-muted-foreground">Revisione {doc.revision_number}</span>
        <Badge testid="delivery-state-badge" cls={readOnly ? "bg-slate-200 text-slate-700 ml-auto" : "bg-amber-100 text-amber-800 ml-auto"}>
          {readOnly ? <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" />{STATE_LABELS.finalized}</span> : STATE_LABELS.draft}
        </Badge>
      </div>

      {/* Linked prestation */}
      {doc.session_id && (
        <button type="button" data-testid="delivery-linked-session" onClick={openLesson}
          className="w-full text-left rounded-2xl border border-border bg-secondary/30 p-3 hover:border-primary transition-colors">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><CalendarClock className="h-3.5 w-3.5" />Lezione collegata</div>
          <div className="mt-0.5">{ls ? <LinkedSessionLine ls={ls} /> : <div className="text-sm text-muted-foreground">Caricamento…</div>}</div>
          <span className="text-sm text-primary font-semibold inline-flex items-center gap-1 mt-1">Apri lezione →</span>
        </button>
      )}

      {conflict && (
        <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 flex items-start gap-2" data-testid="delivery-conflict-banner">
          <div className="flex-1 text-sm text-destructive">
            Questa consegna è stata modificata altrove. Le tue modifiche locali non sono state sovrascritte. Ricarica per allinearti alla versione sul server (perderai le modifiche locali non salvate).
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" data-testid="delivery-conflict-reload" onClick={() => reload()}>Ricarica</Button>
        </div>
      )}

      {/* Title */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="text-sm font-medium">Titolo</div>
        {readOnly
          ? <div className="mt-1 font-semibold break-words" data-testid="delivery-title-ro">{title || "(senza titolo)"}</div>
          : <input data-testid="delivery-title" className={`${inputCls} mt-1`} value={title}
              onFocus={(e) => { if (titleSelRef.current.shouldSelect()) { e.target.select(); titleSelRef.current.consume(); } }}
              onChange={(e) => { titleSelRef.current.consume(); onTitle(e.target.value); }} />}
      </div>

      {/* Blocks */}
      <div className="space-y-3" data-testid="delivery-blocks">
        {blocks.map((b, i) => (
          <div key={b.id} className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid={`delivery-block-${i}`}>
            <div className="flex items-center gap-2 min-h-[2rem]">
              {b.type === "EXERCISE" && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"><Dumbbell className="h-3.5 w-3.5" />Esercizio</span>
              )}
              {!readOnly && (
                <div className="ml-auto flex items-center gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" data-testid={`delivery-block-up-${i}`} disabled={i === 0} onClick={() => doMove(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" data-testid={`delivery-block-down-${i}`} disabled={i === blocks.length - 1} onClick={() => doMove(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                  {blockHasContent(b) ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-destructive" data-testid={`delivery-block-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Eliminare questo blocco?</AlertDialogTitle>
                          <AlertDialogDescription>Il contenuto di questo blocco verrà rimosso dalla consegna.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Annulla</AlertDialogCancel>
                          <AlertDialogAction data-testid={`delivery-block-del-confirm-${i}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => doRemove(b.id)}>Elimina</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full text-destructive" data-testid={`delivery-block-del-${i}`} onClick={() => doRemove(b.id)}><Trash2 className="h-4 w-4" /></Button>
                  )}
                </div>
              )}
            </div>

            {b.type === "TEXT" ? (
              <>
                {readOnly ? (
                  <>
                    {b.title && <div className="font-semibold break-words" data-testid={`delivery-text-title-ro-${i}`}>{b.title}</div>}
                    {b.content && <div className="text-sm whitespace-pre-line break-words" data-testid={`delivery-text-content-ro-${i}`}>{b.content}</div>}
                  </>
                ) : (
                  <>
                    <input data-testid={`delivery-text-title-${i}`} className={inputCls} placeholder="Titolo sezione" value={b.title || ""} onChange={(e) => editBlockField(b.id, "title", e.target.value)} />
                    <LongText testid={`delivery-text-content-${i}`} placeholder="Scrivi qui…" value={b.content} onChange={(v) => editBlockField(b.id, "content", v)} />
                  </>
                )}
              </>
            ) : readOnly ? (
              <div className="space-y-2" data-testid={`delivery-ex-ro-${i}`}>
                {EXERCISE_PREVIEW_FIELDS
                  .filter(([k]) => ((b[k] || "") + "").trim())
                  .map(([k, label]) => (
                    <div key={k}>
                      <div className="text-sm font-medium text-muted-foreground">{label}</div>
                      <div className={`${k === "name" ? "font-semibold" : "text-sm"} whitespace-pre-line break-words`} data-testid={`delivery-ex-${k}-ro-${i}`}>{b[k]}</div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="space-y-3">
                {EXERCISE_FIELDS.map(([k, label, long]) => (
                  <div key={k}>
                    <div className={`text-sm ${k === "name" ? "font-semibold" : "font-medium text-muted-foreground"}`}>{label}</div>
                    {long ? (
                      <LongText testid={`delivery-ex-${k}-${i}`} value={b[k]} onChange={(v) => editBlockField(b.id, k, v)} />
                    ) : (
                      <input data-testid={`delivery-ex-${k}-${i}`} className={`${inputCls} mt-0.5`} value={b[k] || ""} onChange={(e) => editBlockField(b.id, k, e.target.value)} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        {blocks.length === 0 && <p className="text-sm text-muted-foreground" data-testid="delivery-blocks-empty">Nessun blocco. Aggiungine uno qui sotto.</p>}
      </div>

      {!readOnly && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="delivery-add-actions">
            <Button variant="outline" className="rounded-full" data-testid="delivery-add-text" onClick={doAddText}><Plus className="h-4 w-4 mr-1" />Blocco di testo</Button>
            <Button variant="outline" className="rounded-full" data-testid="delivery-add-exercise" onClick={doAddExercise}><Plus className="h-4 w-4 mr-1" />Esercizio</Button>
            <Button variant="outline" className="rounded-full" data-testid="delivery-import-btn" onClick={() => setImportOpen(true)}><Download className="h-4 w-4 mr-1" />Aggiungi da K9</Button>
          </div>

          <div className="pt-2 flex flex-wrap items-center gap-2">
            <Button variant="outline" className="rounded-full" data-testid="delivery-preview-btn" onClick={() => setPreviewOpen(true)}><Eye className="h-4 w-4 mr-1" />Anteprima cliente</Button>
            <Button className="rounded-full" data-testid="delivery-finalize-btn" onClick={() => setFinalizeOpen(true)}><Lock className="h-4 w-4 mr-1" />Finalizza</Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full text-destructive ml-auto" data-testid="delivery-delete-draft"><Trash2 className="h-4 w-4 mr-1" />Elimina bozza</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Eliminare questa bozza?</AlertDialogTitle>
                  <AlertDialogDescription>La consegna e il suo contenuto verranno eliminati definitivamente. Questa operazione non può essere annullata.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annulla</AlertDialogCancel>
                  <AlertDialogAction data-testid="delivery-delete-draft-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete}>Elimina bozza</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}

      {readOnly && (
        <div className="rounded-2xl border border-border bg-secondary/20 p-4 space-y-3" data-testid="delivery-finalized-panel">
          <div className="text-sm text-muted-foreground" data-testid="delivery-finalized-note">
            Questa revisione è finalizzata{doc.finalized_at ? ` il ${fmtTimestamp(doc.finalized_at)}` : ""} e non è modificabile.
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="rounded-full" data-testid="delivery-preview-btn" onClick={() => setPreviewOpen(true)}><Eye className="h-4 w-4 mr-1" />Anteprima cliente</Button>
            <ShareActions data={{ title, blocks, revision_number: doc.revision_number }} />
            <Button className="rounded-full" data-testid="delivery-new-revision-btn" onClick={runRevision}><Plus className="h-4 w-4 mr-1" />Crea nuova revisione</Button>
          </div>
        </div>
      )}

      {/* Persistent status/nav bar: keyboard-aware so save state stays visible while typing.
          Desktop: sticky at the bottom. Mobile keyboard open: pinned just above the keyboard. */}
      <DeliveryStatusBar readOnly={readOnly} saveState={saveState} onBack={handleBack} onRetry={() => doSave().catch(() => {})} />

      {importOpen && <ImportDialog dogId={doc.dog_id} onClose={() => setImportOpen(false)} onImport={runImport} />}
      {previewOpen && <PreviewDialog data={{ title, blocks, revision_number: doc.revision_number }} onClose={() => setPreviewOpen(false)} />}

      <AlertDialog open={finalizeOpen} onOpenChange={setFinalizeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizzare questa consegna?</AlertDialogTitle>
            <AlertDialogDescription>Dopo la finalizzazione questa revisione non sarà più modificabile. Per apportare correzioni dovrai creare una nuova revisione.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delivery-finalize-confirm" onClick={runFinalize}>Finalizza</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={exitPrompt} onOpenChange={setExitPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Modifiche non salvate</AlertDialogTitle>
            <AlertDialogDescription>Le ultime modifiche non sono state salvate. Vuoi riprovare o uscire senza salvare?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delivery-exit-retry">Riprova</AlertDialogCancel>
            <AlertDialogAction data-testid="delivery-exit-discard" onClick={() => { dirtyRef.current = false; const a = pendingLeave.current || onBack; pendingLeave.current = null; a(); }}>Esci senza salvare</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ---------------- List + root ----------------
export const ClientDeliveriesTab = ({ dogId, guardRef }) => {
  const [, setSearchParams] = useSearchParams();
  const [view, setView] = useState("list");
  const [openId, setOpenId] = useState(null);
  const [items, setItems] = useState(null);
  const [sessionMap, setSessionMap] = useState({});
  const [delDraft, setDelDraft] = useState(null);
  const [preselectTitle, setPreselectTitle] = useState(false);
  const [histFamily, setHistFamily] = useState(null);

  const confirmDeleteDraft = async () => {
    if (!delDraft) return;
    try { await api.delete(`/client-deliveries/${delDraft.id}`); toast.success("Bozza eliminata"); setDelDraft(null); load(); }
    catch (e) { toast.error(apiErr(e, "Eliminazione non riuscita.")); }
  };

  const load = useCallback(() => {
    apiGet(`/dogs/${dogId}/client-deliveries`).then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, [dogId]);
  useEffect(() => { if (view === "list") load(); }, [view, load]);

  // Resolve canonical sessions for session-linked cards (read-only).
  useEffect(() => {
    if (view !== "list" || !items) return;
    const ids = [...new Set(items.filter((i) => i.document_type === "session" && i.session_id).map((i) => i.session_id))];
    const missing = ids.filter((id) => !(id in sessionMap));
    if (missing.length === 0) return;
    Promise.all(missing.map((id) => apiGet(`/sessions/${id}`).then(({ data }) => [id, data]).catch(() => [id, null])))
      .then((pairs) => setSessionMap((prev) => ({ ...prev, ...Object.fromEntries(pairs) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, items]);

  const openEditor = (id, preselect = false) => { setPreselectTitle(preselect); setOpenId(id); setView("editor"); };
  const openLesson = (sid) => setSearchParams({ tab: "lezioni", session: sid });

  if (view === "create") return <CreateFlow dogId={dogId} onCancel={() => setView("list")} onCreated={(d) => openEditor(d.id, true)} />;
  if (view === "editor" && openId) return <DeliveryEditor key={openId} deliveryId={openId} titlePreselect={preselectTitle} onOpenLesson={openLesson} guardRef={guardRef} onNavigate={(id) => openEditor(id)} onBack={() => { setOpenId(null); setView("list"); }} />;

  if (items === null) return <p className="text-sm text-muted-foreground" data-testid="delivery-list-loading">Caricamento…</p>;

  return (
    <div className="space-y-4" data-testid="client-deliveries-tab">
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-6 text-center" data-testid="delivery-empty">
          <h3 className="font-head text-base font-bold">Non hai ancora creato consegne per questo cane.</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">Qui raccoglierai il materiale preparato per il proprietario: resoconti, piani di lavoro, schede esercizi e indicazioni.</p>
          <Button className="rounded-full mt-4" data-testid="delivery-new-btn" onClick={() => setView("create")}><Plus className="h-4 w-4 mr-1" />Nuova consegna</Button>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-head text-lg font-bold">Consegne</h3>
            <Button size="sm" className="rounded-full" data-testid="delivery-new-btn" onClick={() => setView("create")}><Plus className="h-4 w-4 mr-1" />Nuova consegna</Button>
          </div>
          <div className="space-y-2">
            {groupDeliveryFamilies(items).map((fam) => {
              const it = fam.latest;
              const ls = it.document_type === "session" ? formatLinkedSession(sessionMap[it.session_id]) : null;
              return (
                <div key={fam.delivery_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`delivery-card-${it.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" data-testid={`delivery-open-${it.id}`} onClick={() => openEditor(it.id)} className="min-w-0 flex-1 text-left">
                      <div className="font-semibold break-words">{it.title || "(senza titolo)"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        <span>{templateLabel(it.template_type)}</span>
                        <span>·</span>
                        {ls ? (
                          <span data-testid={`delivery-card-linked-${it.id}`}>{ls.title}{ls.date ? ` · ${fmtDate(ls.date)}` : ""}{ls.time ? ` · ${ls.time}` : ""}</span>
                        ) : (
                          <span>{CONTEXT_LABELS[it.document_type] || "Generale"}</span>
                        )}
                        <span>·</span>
                        <span>Revisione {it.revision_number}</span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge testid={`delivery-card-state-${it.id}`} cls={it.state === "finalized" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"}>
                        {STATE_LABELS[it.state] || it.state}
                      </Badge>
                      {it.state === "draft" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" data-testid={`delivery-card-menu-${it.id}`} aria-label="Altre azioni" className="p-1.5 rounded-full text-muted-foreground hover:bg-secondary"><MoreVertical className="h-4 w-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="text-destructive focus:text-destructive" data-testid={`delivery-card-delete-${it.id}`} onClick={() => setDelDraft(it)}><Trash2 className="h-4 w-4 mr-2" />Elimina bozza</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  {it.document_type === "session" && it.session_id && (
                    <button type="button" data-testid={`delivery-open-lesson-${it.id}`} onClick={() => openLesson(it.session_id)}
                      className="mt-2 inline-flex items-center gap-1 text-sm text-primary font-semibold hover:underline">
                      Apri lezione →
                    </button>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <span className="text-[11px] text-muted-foreground">Aggiornato {fmtTimestamp(it.updated_at)}</span>
                    {fam.count > 1 && (
                      <button type="button" data-testid={`delivery-history-${fam.delivery_id}`} onClick={() => setHistFamily(fam)}
                        className="text-xs font-semibold text-primary inline-flex items-center gap-1 hover:underline">
                        {fam.count} revisioni · Storico
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!histFamily} onOpenChange={(v) => { if (!v) setHistFamily(null); }}>
        <DialogContent data-testid="delivery-history-dialog" className="max-w-md w-[calc(100%-1.5rem)] max-h-[85dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>Storico revisioni</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(histFamily?.revisions || []).map((r) => (
              <button key={r.id} type="button" data-testid={`history-rev-${r.id}`}
                onClick={() => { setHistFamily(null); openEditor(r.id); }}
                className="w-full text-left rounded-xl border border-border p-3 hover:border-primary transition-colors flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold">Revisione {r.revision_number}</div>
                  <div className="text-xs text-muted-foreground">{r.state === "finalized" && r.finalized_at ? `Finalizzata ${fmtTimestamp(r.finalized_at)}` : `Aggiornata ${fmtTimestamp(r.updated_at)}`}</div>
                </div>
                <Badge testid={`history-rev-state-${r.id}`} cls={r.state === "finalized" ? "bg-slate-200 text-slate-700" : "bg-amber-100 text-amber-800"}>{STATE_LABELS[r.state] || r.state}</Badge>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delDraft} onOpenChange={(v) => { if (!v) setDelDraft(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa bozza?</AlertDialogTitle>
            <AlertDialogDescription>La consegna e il suo contenuto verranno eliminati definitivamente. Questa operazione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="delivery-card-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDeleteDraft}>Elimina bozza</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
